import React from "react";
import {DataGridProps} from "./Types/DataGridProps";
import {DefaultDataGridState} from "./Types/DefaultDataGridState";
import Field from "./Field";
import {RefreshSettings} from "./Types/ProviderProps";
import Table from "./Table/Table";
import ListFieldDataProvider, {ListFieldDataProviderProps} from "./ListProviders/ListFieldDataProvider";
import ComponentListDataProvider, {ComponentListDataProviderProps} from "./ListProviders/ComponentListDataProvider";
import ListDataProvider from "./ListProviders/ListDataProvider";
import ComponentConfig from "../../ComponentConfig";
import Icon from "../../Icon";
import PlaceHolderComponent from "./Table/PlaceHolderComponent";
import StateManager from "./Managers/StateManager";

class DataGridComp extends React.Component<DataGridProps, DefaultDataGridState> {

    private el: any;

    constructor(props: DataGridProps) {
        super(props);
        this.getFields = this.getFields.bind(this);
        this.refresh = this.refresh.bind(this);
    }

    getFields() {
        if (this.props.fields) {
            return Object.entries(this.props.fields).reduce((all, entry) => ([
                ...all,
                {
                    ...entry[1],
                    props: {
                        ...(entry[1].props || {}),
                        field: entry[0]
                    }
                }
            ]), []);
        }
        return React.Children.map(this.props.children || [], (child: React.ReactElement, idx) => {
            if (!child) return;
            if (child?.type?.prototype instanceof Field || child.type === Field) {
                return child;
            }
        }).filter(Boolean);
    }

    refresh(settings?: RefreshSettings) {
        return this.el && this.el.refresh(settings);
    }

    render() {
        const Type = this.props.type || Table;
        const CurrentStateManager = this.props.stateManager;
        const Comp = (
            <CurrentStateManager
                ref={(el) => this.el = el}
                {...this.props}
                generateRequest={() => {
                    console.error("No generateRequest props!");
                }}
                loadData={() => {
                    console.error("No loadData props!");
                }}
                getFields={this.getFields}
            >
                <Type {...this.props} getFields={this.getFields} />
            </CurrentStateManager>
        );
        let listFieldProps = this.props as ListFieldDataProviderProps;
        let componentListProps = this.props as ComponentListDataProviderProps;
        if (listFieldProps.componentListFieldConfig && (this.props.parentid || this.props.data?.[this.props.componentConfig.idfield])) {
            return <ListFieldDataProvider {...this.props} refresh={this.refresh} getFields={this.getFields}>
                {Comp}
            </ListFieldDataProvider>
        } else if (componentListProps.componentConfig && !listFieldProps.componentListFieldConfig) {
            return <ComponentListDataProvider {...this.props} refresh={this.refresh} getFields={this.getFields}>
                {Comp}
            </ComponentListDataProvider>
        } else {
            return <ListDataProvider {...this.props} refresh={this.refresh} getFields={this.getFields}>
                {Comp}
            </ListDataProvider>
        }
    }
}

const ListFieldConfigLoader = (props: DataGridProps) => {
    let listFieldProps = props as ListFieldDataProviderProps;
    let componentListProps = props as ComponentListDataProviderProps;
    const f = listFieldProps.listfield || listFieldProps.name;
    const field = listFieldProps.listfieldtype || componentListProps.componentConfig?.fielddefs?.[f]?.object || componentListProps?.componentConfig?.fielddefs?.[f]?.objects;

    if (field) {
        return <ComponentConfig componentName={field} componentConfigAs="componentListFieldConfig" componentConfig={componentListProps.componentConfig}>
            {props.children}
        </ComponentConfig>
    } else {
        console.error(`Please set the listfieldtype props or define ${f} fielddef in ${componentListProps.componentConfig.componentname}, no object or objects field in fielddef!`);
        return <Icon lib="fa" name="exclamation-triangle" />;
    }
}

ListFieldConfigLoader.placeHolder = PlaceHolderComponent;

let defaultActions: DataGridProps['actions'] = [];
let defaultPlugins: DataGridProps['plugins'] = [];

export const setDefaultActions = (actions: DataGridProps['actions']) => {
    defaultActions = actions;
}

export const setDefaultPlugins = (plugins: DataGridProps['plugins']) => {
    defaultPlugins = plugins;
}

export default class DataGridInternal<Props extends DataGridProps> extends React.Component<Props, any> {

    protected dataGridComp = DataGridComp;

    constructor(props) {
        super(props);
        this.getDefaultStateManager = this.getDefaultStateManager.bind(this);
        this.setDefaultActions = this.setDefaultActions.bind(this);
        this.getDefaultActions = this.getDefaultActions.bind(this);
        this.setDefaultPlugins = this.setDefaultPlugins.bind(this);
        this.getDefaultPlugins = this.getDefaultPlugins.bind(this);
    }

    getDefaultStateManager() {
        return StateManager;
    }

    setDefaultActions(actions: DataGridProps['actions']) {
        defaultActions = actions;
    }

    getDefaultActions(): DataGridProps['actions'] {
        return Array.isArray(defaultActions) ? [...defaultActions] : defaultActions;
    }

    setDefaultPlugins(plugins: DataGridProps['plugins']) {
        defaultPlugins = plugins;
    }

    getDefaultPlugins(): DataGridProps['plugins'] {
        return Array.isArray(defaultPlugins) ? [...defaultPlugins] : defaultPlugins;
    }

    static placeHolder = PlaceHolderComponent;

    private el: DataGridComp;

    refresh = () => {
        return this.el && this.el.refresh();
    }

    refreshBackground = () => {
        return this.el && this.el.refresh({silent: true});
    }

    render() {
        let listFieldProps = this.props as ListFieldDataProviderProps;
        let componentListProps = this.props as ComponentListDataProviderProps;

        let props = {
            ...this.props,
            stateManager: this.props.stateManager || this.getDefaultStateManager(),
            actions: this.props.actions || [...this.getDefaultActions()],
            plugins: this.props.plugins || [...this.getDefaultPlugins()],
            emptyMessage: this.props.emptyMessage === undefined ? "Nincs találat" : this.props.emptyMessage,
        };

        let ret = <this.dataGridComp
            ref={(el) => this.el = el}
            {...props}
        />;
        let plugins = props.plugins;
        if (plugins instanceof Function) {
            plugins = plugins(props);
        }
        plugins?.reverse()?.map((plugin) => {
            if (plugin.type === "provider") {
                ret = <plugin.plugin {...props}>
                    {ret}
                </plugin.plugin>;
            }
        });
        if (listFieldProps.listfield || listFieldProps.name) {
            ret = <ListFieldConfigLoader {...props}>
                {ret}
            </ListFieldConfigLoader>
        }
        if (componentListProps?.component) {
            ret = <ComponentConfig componentName={componentListProps.component}>
                {ret}
            </ComponentConfig>
        }
        return ret;
    }
}